import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { STATUS } from "@/lib/traffic";
import { cn } from "@/lib/utils";

const KM0 = 255;
const KM1 = 428;

export function StatusPill({ status, label, className, testId }) {
  const s = STATUS[status] || STATUS.nodata;
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1", s.bg, s.text, s.ring, className)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", s.dot, (status === "congested") && "animate-pulse")} />
      {label || s.label}
    </span>
  );
}

function Node({ site, x, onHover, hovered, flip, large }) {
  const nav = useNavigate();
  const nb = STATUS[site.dirs?.NB?.status] || STATUS.nodata;
  const sb = STATUS[site.dirs?.SB?.status] || STATUS.nodata;
  const km = site.km ? site.km.toFixed(1) : "F.RD";
  const r = large ? 9 : 7;
  // a radar sits on the carriageway named in its site code; the feeder-road radar (no side) reports both
  const showNB = !site.side || site.side === "NB";
  const showSB = !site.side || site.side === "SB";
  return (
    <g transform={`translate(${x},0)`} className="cursor-pointer" onMouseEnter={() => onHover(site)} onMouseLeave={() => onHover(null)}
      onClick={() => nav(`/live?site=${site.site_id}`)} data-testid={`radar-node-${site.site_id}`}>
      <line x1="0" y1={showNB ? 34 : 86} x2="0" y2="100" stroke="currentColor" className="text-border" strokeWidth="1" />
      {showNB && <circle cx="0" cy="34" r={hovered ? r + 2 : r} fill={nb.hex} className="transition-all duration-200" stroke="white" strokeWidth="2" data-testid={`radar-node-${site.site_id}-NB`} />}
      {showSB && <circle cx="0" cy="86" r={hovered ? r + 2 : r} fill={sb.hex} className="transition-all duration-200" stroke="white" strokeWidth="2" data-testid={`radar-node-${site.site_id}-SB`} />}
      {site.offline && <text y={showNB ? 38 : 90} textAnchor="middle" fill="white" style={{ fontSize: large ? 11 : 9, fontWeight: 700 }}>!</text>}
      {!site.offline && large && <text y={showNB ? 37.5 : 89.5} textAnchor="middle" fill="white" style={{ fontSize: 7.5, fontWeight: 700 }}>{site.dirs?.[site.side || "NB"]?.n ? site.dirs[site.side || "NB"].spd : ""}</text>}
      <text y={flip ? 120 : 109} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: large ? 9 : 8, fontFamily: "JetBrains Mono, monospace" }}>{km}</text>
      <text y={flip ? 109 : 120} textAnchor="middle" className="fill-foreground" style={{ fontSize: large ? 9.5 : 8.5, fontWeight: 600 }}>{site.site_id.replace("VIDS", "R")}</text>
    </g>
  );
}

export function StripMap({ sites, large }) {
  const [hover, setHover] = useState(null);
  const W = large ? 1080 : 1100;
  const placed = useMemo(() => {
    const withKm = (sites || []).map((s) => ({ ...s, _km: s.km ?? KM1 - 1.2 }));
    // spread out nodes that sit too close to each other on the KM axis; alternate label rows for tight pairs
    const sorted = [...withKm].sort((a, b) => a._km - b._km);
    let lastX = -Infinity;
    return sorted.map((s, i) => {
      let x = 40 + ((s._km - KM0) / (KM1 - KM0)) * (W - 80);
      if (x - lastX < 36) x = lastX + 36;
      const flip = i > 0 && x - lastX < 48;
      lastX = x;
      return { ...s, x, flip };
    });
  }, [sites, W]);
  return (
    <div className="relative" data-testid="strip-map">
      <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} 130`} className="w-full min-w-[720px]" role="img" aria-label="LPT2 highway strip map">
        <defs>
          <linearGradient id="road" x1="0" x2="1">
            <stop offset="0" stopColor="#334155" />
            <stop offset="1" stopColor="#1e293b" />
          </linearGradient>
        </defs>
        <rect x="20" y="23" width={W - 40} height="22" rx="11" fill="url(#road)" opacity="0.9" />
        <rect x="20" y="75" width={W - 40} height="22" rx="11" fill="url(#road)" opacity="0.9" />
        <line x1="30" y1="34" x2={W - 30} y2="34" stroke="white" strokeDasharray="8 10" strokeWidth="1" opacity="0.5" />
        <line x1="30" y1="86" x2={W - 30} y2="86" stroke="white" strokeDasharray="8 10" strokeWidth="1" opacity="0.5" />
        <text x="22" y="14" className="fill-muted-foreground" style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1 }}>NORTHBOUND → Kuala Terengganu</text>
        <text x={W - 22} y="70" textAnchor="end" className="fill-muted-foreground" style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1 }}>Jabor / Kuantan ← SOUTHBOUND</text>
        <text x="22" y="70" className="fill-muted-foreground" style={{ fontSize: 9 }}>KM258.7</text>
        <text x={W - 22} y="14" textAnchor="end" className="fill-muted-foreground" style={{ fontSize: 9 }}>KM424.7 · Kuala Terengganu feeder road</text>
        {placed.map((s) => <Node key={s.site_id} site={s} x={s.x} flip={s.flip} large={large} onHover={setHover} hovered={hover?.site_id === s.site_id} />)}
      </svg>
      </div>
      {hover && (
        <div className="pointer-events-none absolute left-1/2 top-0 z-10 w-72 -translate-x-1/2 -translate-y-[calc(100%+6px)] rounded-2xl border border-border/70 bg-popover p-3 text-xs shadow-lg" data-testid="strip-map-tooltip">
          <div className="flex items-center justify-between">
            <span className="font-semibold">{hover.name}</span>
            <span className="num text-muted-foreground">{hover.ip}</span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {["NB", "SB"].filter((d) => !hover.side || hover.side === d).map((d) => (
              <div key={d} className={cn("rounded-xl bg-secondary/60 p-2", hover.side && "col-span-2")}>
                <div className="flex items-center justify-between"><span className="font-medium">{d === "NB" ? "Northbound" : "Southbound"}</span><StatusPill status={hover.dirs?.[d]?.status} /></div>
                <div className="mt-1 num text-muted-foreground">{hover.dirs?.[d]?.n ? `${hover.dirs[d].spd} km/h` : "—"} · {hover.dirs?.[d]?.n || 0} veh / 5 min</div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-muted-foreground">{hover.offline ? `Offline — ${hover.offline_reason || ""}` : `Last cycle ${hover.last_ts?.slice(11, 16) || "—"}${hover.ping_rtt_ms != null ? ` · ping ${hover.ping_rtt_ms} ms` : ""}`}</div>
        </div>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        {Object.entries(STATUS).filter(([k]) => k !== "nodata").map(([k, s]) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className={cn("h-2 w-2 rounded-full", s.dot)} />{s.label}</span>
        ))}
        <span className="ml-auto hidden sm:inline">Each radar monitors the carriageway in its name · number = current km/h · click a radar for its live view</span>
      </div>
    </div>
  );
}
